var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { Stacker, Checkbox, StarFilledIcon, TypographyBody, LazyImage, Styler, ChevronDownIcon, ChevronUpIcon, Button, useHoneycombTheme, ConversationIcon, } from '@honeycomb/ui-core';
import { useGlobalContext, FilterCategoryEnum, usePrevious } from '@honeycomb/data';
import { FilterAccordion } from './FilterAccordion';
import { toggleArrayValue } from './utils';
import { useDebounce } from '../../utils/useDebounce';
const MAX_ITEMS = 6;
export function CheckboxGroupFilter(_a) {
    var { items, onChange, heading, id, filteredRecordCount } = _a, other = __rest(_a, ["items", "onChange", "heading", "id", "filteredRecordCount"]);
    const { resourceStrings: { HC_IMG_TRIPADVISORRATING_URL }, } = useGlobalContext();
    const valuesFromProp = items.filter((option) => option.selected).map((option) => option.id);
    const prevValuesFromProp = usePrevious(valuesFromProp);
    const [showAll, setShowAll] = React.useState(false);
    // To make filter editing more performant, we update local state and debounce changes to the parent.
    const [selectedValues, setSelectedValues] = React.useState(valuesFromProp);
    const debouncedOnChange = useDebounce(({ itemId, checked, group, label }) => {
        onChange(itemId, checked, group, label);
    }, 500);
    const handleChange = (event) => {
        const element = event.target;
        const { name: itemId, checked, ariaLabel: label } = element;
        const newTypes = toggleArrayValue(itemId, selectedValues);
        setSelectedValues(newTypes);
        // Debounce the change
        debouncedOnChange({ itemId, checked, group: id, label: label !== null && label !== void 0 ? label : '' });
    };
    // Handle reset scenario
    React.useEffect(() => {
        if ((prevValuesFromProp && valuesFromProp.length !== prevValuesFromProp.length) ||
            !valuesFromProp.every((item) => prevValuesFromProp === null || prevValuesFromProp === void 0 ? void 0 : prevValuesFromProp.includes(item))) {
            setSelectedValues(valuesFromProp);
        }
    }, [prevValuesFromProp, valuesFromProp]);
    const visibleItems = showAll ? items : items.slice(0, MAX_ITEMS);
    const hiddenCount = showAll ? 0 : items.length - MAX_ITEMS;
    const { theme } = useHoneycombTheme();
    return (React.createElement(FilterAccordion, Object.assign({ id: id, heading: heading }, other),
        React.createElement(React.Fragment, null,
            visibleItems.slice(0).map((option) => {
                const hideLabel = id === FilterCategoryEnum.TripAdvisorRating;
                const selected = selectedValues.includes(option.id);
                const disabled = option.count === 0 && filteredRecordCount > 0;
                return (React.createElement(Stacker, { component: "label", direction: "row", spacing: 3, alignItems: "center", "data-id": "filter-checkbox", "data-key": option.label, key: `filters-${id}-${option.id}`, mih: 48 },
                    React.createElement(Checkbox, { name: option.id, size: "s", disabled: disabled, onChange: handleChange, checked: selected, "data-checked": selected || undefined, "aria-label": option.label }),
                    React.createElement(Stacker, { direction: "row", spacing: 2, alignItems: "center", opacity: disabled ? theme.interaction.disabledOpacity : undefined, flexShrink: "unset" },
                        option.label && !hideLabel && (React.createElement(TypographyBody, { component: "div", flexShrink: "unset" }, option.label)),
                        (id === FilterCategoryEnum.StarRating || id === FilterCategoryEnum.TrustYouRating) &&
                            (id === FilterCategoryEnum.TrustYouRating ? (React.createElement(ConversationIcon, { fill: "accent.base" })) : (React.createElement(StarFilledIcon, { size: "xs" }))),
                        id === FilterCategoryEnum.TripAdvisorRating && (
                        // The negative margin is used to offset the padding in the image
                        React.createElement(Styler, { ml: "-6px", display: "inline-flex" },
                            React.createElement(LazyImage, { alt: "Rating", height: "auto", src: HC_IMG_TRIPADVISORRATING_URL.replace('IMGPATH', parseInt(option.label).toFixed(1)) }))))));
            }),
            items.length > MAX_ITEMS && (React.createElement(Button, { color: "neutral", size: "s", icon: !showAll ? React.createElement(ChevronDownIcon, { size: "xs" }) : React.createElement(ChevronUpIcon, { size: "xs" }), onClick: () => setShowAll(!showAll), fullWidth: true, iconPosition: "end", mt: 4 }, !showAll ? `Show ${hiddenCount} more` : 'Show less')))));
}
